import { useState } from 'react';
import { toast } from 'sonner';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../ui/alert-dialog';
import { DlqReasonBadge } from './badges';
import { formatDateTime } from './format';
import { DlqMessageDialog } from './DlqMessageDialog';
import { apiService, DlqMessage } from '../../../services/api';
import { extractErrorMessage } from '../../../utils/errorHandler';

export interface DeadLetterQueueTabProps {
  messages: DlqMessage[];
  total: number;
  loading: boolean;
  error: string | null;
  onReload: () => void;
}

/** Tab „Dead Letter Queue" — Liste der `service-errors-dlq`-Nachrichten, Requeue, Verwerfen. */
export function DeadLetterQueueTab({ messages, total, loading, error, onReload }: DeadLetterQueueTabProps) {
  const [selectedMessage, setSelectedMessage] = useState<DlqMessage | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const [requeueAllPending, setRequeueAllPending] = useState(false);

  const openDetail = (message: DlqMessage) => {
    setSelectedMessage(message);
    setDetailOpen(true);
  };

  const handleRequeueAll = async () => {
    try {
      setRequeueAllPending(true);
      const result = await apiService.requeueAllDlqMessages();
      toast.success(`${result.requeued} Nachrichten erneut eingereiht`);
      onReload();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Erneutes Einreihen fehlgeschlagen'));
    } finally {
      setRequeueAllPending(false);
    }
  };

  const handleRequeueOne = async (message: DlqMessage) => {
    try {
      setActionPending(true);
      await apiService.requeueDlqMessage(message.id);
      toast.success('Nachricht erneut eingereiht');
      setDetailOpen(false);
      onReload();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Erneutes Einreihen fehlgeschlagen'));
    } finally {
      setActionPending(false);
    }
  };

  const handleDeleteOne = async (message: DlqMessage) => {
    try {
      setActionPending(true);
      await apiService.deleteDlqMessage(message.id);
      toast.success('Nachricht verworfen');
      setDetailOpen(false);
      onReload();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Verwerfen fehlgeschlagen'));
    } finally {
      setActionPending(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle>Dead Letter Queue</CardTitle>
              <CardDescription>
                {total} Nachrichten in service-errors-dlq — schema-invalide oder endgültig fehlgeschlagene Events,
                TTL 14 Tage.
              </CardDescription>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={total === 0 || loading || requeueAllPending}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Alle {total} erneut einreihen
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Alle {total} Nachrichten erneut einreihen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Die Events durchlaufen Validierung, Kundenzuordnung und Severity-Bewertung erneut.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRequeueAll}>Erneut einreihen</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="flex items-center justify-center p-8 text-red-600">
              <AlertCircle className="mr-2 h-5 w-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={onReload} className="ml-4">
                Erneut versuchen
              </Button>
            </div>
          )}

          {loading && !error && (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="mr-2 h-6 w-6 animate-spin" />
              <span>DLQ-Nachrichten werden geladen...</span>
            </div>
          )}

          {!loading && !error && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Dead-lettered am</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Grund</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Service</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Payload-Auszug</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Zustellversuche</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-gray-500">
                        Keine Nachrichten in der Dead Letter Queue
                      </td>
                    </tr>
                  )}
                  {messages.map((message) => (
                    <tr
                      key={message.id}
                      className="cursor-pointer border-b border-gray-100 hover:bg-gray-50"
                      onClick={() => openDetail(message)}
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                        {formatDateTime(message.deadLetteredAt)}
                      </td>
                      <td className="px-4 py-3">
                        <DlqReasonBadge reason={message.reason} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {message.service ?? <span className="italic text-gray-400">unbekannt</span>}
                      </td>
                      <td className="max-w-xs truncate px-4 py-3 font-mono text-sm text-gray-700">
                        {message.payloadPreview}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{message.retryCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <DlqMessageDialog
        message={selectedMessage}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onRequeue={handleRequeueOne}
        onDelete={handleDeleteOne}
        actionPending={actionPending}
      />
    </div>
  );
}
